import { create } from "zustand";
import { apiFetch } from "@/lib/api";

interface GroupSettings {
  welcomeEnabled: boolean;
  welcomeMessage: string;
  antiSpamEnabled: boolean;
  antiFloodEnabled: boolean;
  badWordsEnabled: boolean;
  linkProtectionEnabled: boolean;
  moderationLevel: "low" | "medium" | "high";
}

interface GroupStats {
  messagesModerated: number;
  warningsIssued: number;
  usersBanned: number;
}

interface GroupConfig {
  id: string;
  chatId: string;
  title: string;
  titleAm: string;
  memberCount: number;
  botIsAdmin: boolean;
  settings: GroupSettings;
  stats: GroupStats;
}

interface ApiGroupConfig {
  id: number;
  telegramGroupId: string;
  title: string;
  memberCount: number;
  isManaged: boolean;
  welcomeMessage: string | null;
}

const DEFAULT_GROUP_SETTINGS: GroupSettings = {
  welcomeEnabled: true,
  welcomeMessage: "",
  antiSpamEnabled: true,
  antiFloodEnabled: true,
  badWordsEnabled: true,
  linkProtectionEnabled: true,
  moderationLevel: "medium",
};

function normalizeGroup(group: ApiGroupConfig): GroupConfig {
  return {
    id: String(group.id),
    chatId: group.telegramGroupId,
    title: group.title,
    titleAm: group.title,
    memberCount: group.memberCount,
    botIsAdmin: group.isManaged,
    settings: {
      ...DEFAULT_GROUP_SETTINGS,
      welcomeMessage: group.welcomeMessage ?? "",
    },
    stats: { messagesModerated: 0, warningsIssued: 0, usersBanned: 0 },
  };
}

interface GroupState {
  groups: GroupConfig[];
  selectedGroup: GroupConfig | null;
  isLoading: boolean;
  error: string | null;
}

interface GroupActions {
  fetchGroups: () => Promise<void>;
  selectGroup: (groupId: string) => void;
  updateGroupSettings: (
    groupId: string,
    settings: Partial<GroupSettings>,
  ) => Promise<void>;
  clearError: () => void;
}

export const useGroupStore = create<GroupState & GroupActions>((set, get) => ({
  groups: [],
  selectedGroup: null,
  isLoading: false,
  error: null,

  fetchGroups: async () => {
    set({ isLoading: true, error: null });

    try {
      const response = await apiFetch<{ groups: ApiGroupConfig[] }>("/api/groups");
      const groups = response.groups.map(normalizeGroup);
      set({ groups, isLoading: false });
    } catch (err) {
      const message = err instanceof Error ? err.message : "Failed to load groups";
      set({ groups: [], isLoading: false, error: message });
    }
  },

  selectGroup: (groupId) => {
    const group = get().groups.find((g) => g.id === groupId) ?? null;
    set({ selectedGroup: group });
  },

  updateGroupSettings: async (groupId, newSettings) => {
    const currentGroup = get().groups.find((group) => group.id === groupId);
    if (!currentGroup) return;
    const optimisticGroup = {
      ...currentGroup,
      settings: { ...currentGroup.settings, ...newSettings },
    };
    set({
      groups: get().groups.map((group) =>
        group.id === groupId ? optimisticGroup : group,
      ),
      selectedGroup: optimisticGroup,
    });

    if (newSettings.welcomeMessage === undefined) return;

    try {
      const updated = await apiFetch<ApiGroupConfig>(`/api/groups/${groupId}`, {
        method: "PUT",
        body: { welcomeMessage: newSettings.welcomeMessage },
      });
      const normalized = normalizeGroup(updated);

      set({
        groups: get().groups.map((group) =>
          group.id === groupId
            ? { ...normalized, settings: optimisticGroup.settings }
            : group,
        ),
        selectedGroup: { ...normalized, settings: optimisticGroup.settings },
      });
    } catch (err) {
      const message = err instanceof Error ? err.message : "Failed to update group settings";
      set({ error: message });
    }
  },

  clearError: () => set({ error: null }),
}));

export type { GroupConfig, GroupSettings, GroupStats };
